/**
 * GeometrySerializer
 * Converts Three.js Object3D scene graphs into typed arrays for worker transfer
 * Uses Transferable objects for zero-copy postMessage
 */

import * as THREE from 'three'
import type { SerializedMesh, SerializedAircraftGeometry } from './collision.worker.types'
import { shouldUseMultiComponent } from '../CollisionVolumeFactory'

/**
 * Serialize a single Three.js Mesh for worker transfer
 * @param rootInverse - inverse of the aircraft root's world matrix (optional).
 *   When provided, the stored matrix is relative to the aircraft root rather than
 *   absolute world space. This prevents position-doubling when the worker later
 *   computes `newAircraftTransform * meshRelativeMatrix`.
 */
function serializeMesh(mesh: THREE.Mesh, rootInverse?: THREE.Matrix4): SerializedMesh | null {
  const geometry = mesh.geometry
  if (!geometry.attributes.position) return null

  const posAttr = geometry.attributes.position

  // Extract position data correctly — handles both flat and interleaved buffers.
  // Meshopt-compressed GLTF models use InterleavedBufferAttribute where posAttr.array
  // contains ALL vertex attributes (positions, normals, UVs, etc.) interleaved.
  // We must use getX/getY/getZ to read only position components.
  const vertexCount = posAttr.count
  const positions = new Float32Array(vertexCount * 3)
  for (let i = 0; i < vertexCount; i++) {
    positions[i * 3] = posAttr.getX(i)
    positions[i * 3 + 1] = posAttr.getY(i)
    positions[i * 3 + 2] = posAttr.getZ(i)
  }

  // Copy index buffer if present
  let indices: Uint32Array | undefined
  if (geometry.index) {
    const indexArray = geometry.index.array
    indices = new Uint32Array(indexArray.length)
    indices.set(indexArray as Uint32Array | Uint16Array)
  }

  mesh.updateMatrixWorld(true)
  const worldMatrix = new Float32Array(16)

  if (rootInverse) {
    // Bake the relative transform (mesh-to-aircraft-root) into vertex positions.
    // This puts vertices in aircraft-local space (meters), so the worker uses identity
    // for the mesh matrix.
    const relativeMatrix = new THREE.Matrix4().copy(rootInverse).multiply(mesh.matrixWorld)
    const v = new THREE.Vector3()
    for (let i = 0; i < positions.length; i += 3) {
      v.set(positions[i], positions[i + 1], positions[i + 2])
      v.applyMatrix4(relativeMatrix)
      positions[i] = v.x
      positions[i + 1] = v.y
      positions[i + 2] = v.z
    }
    // Identity matrix — positions are already in aircraft-local space
    new THREE.Matrix4().identity().toArray(worldMatrix)
  } else {
    // Wall/obstacle meshes: keep as-is (already pre-transformed)
    worldMatrix.set(mesh.matrixWorld.elements)
  }

  return { positions, indices, worldMatrix }
}

/**
 * Serialize an entire aircraft Object3D for worker transfer
 * Collects all meshes in the scene graph
 */
export function serializeAircraftForWorker(
  id: string,
  typeId: string,
  object: THREE.Object3D
): SerializedAircraftGeometry {
  object.updateMatrixWorld(true)

  // Compute inverse of the aircraft root's world matrix once.
  // Each child mesh matrix will be stored *relative* to this root,
  // so the worker can correctly apply: newTransform * relativeMatrix
  const det = object.matrixWorld.determinant()
  if (Math.abs(det) < 1e-30) {
    console.warn(`[GeometrySerializer] Near-singular aircraft matrixWorld for ${id}, det=${det}`)
  }
  const rootInverse = object.matrixWorld.clone().invert()

  const meshes: SerializedMesh[] = []

  object.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      const serialized = serializeMesh(child, rootInverse)
      if (serialized) {
        meshes.push(serialized)
      }
    }
  })

  return {
    id,
    typeId,
    meshes,
    useMultiComponent: shouldUseMultiComponent(object),
  }
}

/**
 * Serialize wall meshes (already pre-transformed with identity matrix world)
 * These meshes have geometry baked with transforms, so worldMatrix is identity
 */
export function serializeWallMeshes(
  meshes: THREE.Mesh[]
): SerializedMesh[] {
  return meshes
    .map((mesh) => serializeMesh(mesh))
    .filter((m): m is SerializedMesh => m !== null)
}

/**
 * Collect all Transferable buffers from serialized aircraft geometry
 * These buffers will be zero-copy transferred to the worker
 */
export function collectTransferables(
  aircraft: SerializedAircraftGeometry[]
): Transferable[] {
  const transferables: Transferable[] = []

  for (const a of aircraft) {
    for (const mesh of a.meshes) {
      transferables.push(mesh.positions.buffer)
      if (mesh.indices) {
        transferables.push(mesh.indices.buffer)
      }
      transferables.push(mesh.worldMatrix.buffer)
    }
  }

  return transferables
}

/**
 * Collect transferables from serialized wall meshes
 */
export function collectWallTransferables(
  meshes: SerializedMesh[]
): Transferable[] {
  const transferables: Transferable[] = []

  for (const mesh of meshes) {
    transferables.push(mesh.positions.buffer)
    if (mesh.indices) {
      transferables.push(mesh.indices.buffer)
    }
    transferables.push(mesh.worldMatrix.buffer)
  }

  return transferables
}

/**
 * Read current transforms from scene objects for a collision check
 * Returns compact transform data (position + quaternion per aircraft)
 */
export function readAircraftTransforms(
  aircraftIds: string[],
  sceneObjects: Map<string, THREE.Object3D>
): Array<{ id: string; px: number; py: number; pz: number; qx: number; qy: number; qz: number; qw: number; sx: number; sy: number; sz: number }> {
  const transforms: Array<{ id: string; px: number; py: number; pz: number; qx: number; qy: number; qz: number; qw: number; sx: number; sy: number; sz: number }> = []

  for (const id of aircraftIds) {
    const obj = sceneObjects.get(id)
    if (!obj) continue

    obj.updateMatrixWorld(true)
    const pos = obj.position
    const quat = obj.quaternion
    const scale = obj.scale

    transforms.push({
      id,
      px: pos.x,
      py: pos.y,
      pz: pos.z,
      qx: quat.x,
      qy: quat.y,
      qz: quat.z,
      qw: quat.w,
      sx: scale.x,
      sy: scale.y,
      sz: scale.z,
    })
  }

  return transforms
}
